import { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';

import { useTranslation } from 'react-i18next';
import { Provider } from '@vgtv/api-client';
import type { SvpAsset } from '@vgtv/api-client/lib/svp_asset';
import type {
  SvpCategory,
  SvpCategoryAdditional,
} from '@vgtv/api-client/lib/svp_category';

import { loadTags } from '../../services/appnexus';
import { useViewportType } from '../utils/useViewportType';
import { config } from '../../config';
import { selectCategoryById } from '../../modules/categories';
import { selectAssetById } from '../../modules/assets';
import {
  getAssetMetadata,
  getAssetTags,
} from '../../shared/helpers/assetHelpers';
import { formatTagValue as formatTagValueVGTV } from '../../services/appnexus/vgtv/formatTagValue';
import { formatTagValue as formatTagValueAB } from '../../services/appnexus/ab/formatTagValue';
import { usePlayerEvent } from '../utils/usePlayerEvent';
import { useAppSelector } from '../../modules/hooks';

import { DisplayAd } from './DisplayAd/DisplayAd';
import { CompanionAdHeader } from './CompanionAdHeader/CompanionAdHeader';

import * as styles from './AdsContainer.module.scss';

function DisplayAdDisclaimer() {
  const { t } = useTranslation();

  return <div className={styles.disclaimer}>{t('advertisement')}</div>;
}

interface AdsContainerProps {
  className?: string;
  renderCompanionAd?: boolean;
  assetId?: SvpAsset['id'];
  categoryId?: SvpCategory['id'];
  onLoad?: () => void;
  onShow?: () => void;
  initiallyShown?: boolean;
}

const AdsContainer: FC<AdsContainerProps> = ({
  className,
  renderCompanionAd,
  assetId,
  categoryId,
  onLoad,
  onShow,
  initiallyShown = false,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [inStreamCreativeAd, setInStreamCreativeAd] = useState<string>();
  const { isMobile } = useViewportType();

  const category = useAppSelector((state) =>
    selectCategoryById(state, config.provider, categoryId)
  ) as SvpCategoryAdditional | undefined;

  const asset = useAppSelector((state) =>
    selectAssetById(state, config.provider, assetId)
  );

  usePlayerEvent('adStarted', ({ meta }) => {
    const adDuration = meta.duration;
    const creativeId = adDuration > 1 ? meta.adId : 'not_sponsored';

    setInStreamCreativeAd(creativeId);
  });

  const loadAds = useCallback(async () => {
    const keywords: Record<string, string | string[]> = {};

    if (config.provider === Provider.VGTV && category) {
      keywords['no-sno-news-tv_category'] = String(category.id);
    }

    if (config.provider === Provider.AB) {
      if (category) {
        keywords['se-aftonbladet-section'] = [
          'webbtv',
          category.additional.shortPath!,
        ];
        keywords['se-generic-section'] = [
          'webbtv',
          category.additional.shortPath!,
        ];
      }
    }

    if (asset) {
      const tags = getAssetTags(asset);

      if (config.provider === Provider.AB) {
        keywords['se-generic-tags'] = tags
          .map((tag) => tag.tag)
          .filter((tag): tag is string => Boolean(tag))
          .map((tag) => formatTagValueAB(tag));
      }

      if (config.provider === Provider.VGTV) {
        keywords['no-sno-news-sponsors'] = tags
          .map((tag) => tag.tag)
          .filter((tag): tag is string => Boolean(tag))
          .map((tag) => formatTagValueVGTV(tag));

        const contentType = getAssetMetadata(asset).contentType;
        if (contentType) {
          keywords['aa-sch-content_type'] = [
            `${formatTagValueVGTV(asset.category.title)}_${contentType}`,
            formatTagValueVGTV(contentType),
          ];
        }
      }
    }

    await loadTags({
      loadCompanionAd: renderCompanionAd,
      keywords,
    });

    setIsLoaded(true);
  }, [category, asset, renderCompanionAd]);

  useEffect(() => {
    if (!isLoaded) {
      loadAds();
    } else if (typeof onLoad === 'function') {
      onLoad();
    }
  }, [loadAds, isLoaded, onLoad]);

  const header =
    isMobile && config.provider === Provider.VGTV ? (
      <CompanionAdHeader />
    ) : (
      <DisplayAdDisclaimer />
    );

  return (
    <div className={styles.container}>
      {renderCompanionAd && (
        <DisplayAd
          targetId="companionAd"
          onShow={onShow}
          header={header}
          className={className}
          initiallyShown={initiallyShown}
          isLoaded={isLoaded}
          isCompanion
          videoCreativeId={inStreamCreativeAd}
        />
      )}
      {config.provider === Provider.VGTV && (
        <DisplayAd targetId="testAd" onShow={onShow} isLoaded={isLoaded} />
      )}
    </div>
  );
};

export { AdsContainer };
